import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import supabase from '../../config/supabaseClient';
import { Link } from 'react-router-dom';
import Form from './Form';
import RaceInformation from './RaceInformation';
import HorseInformation from './HorseInformation';
import SavedHorses from './SavedHorses';
import BreakInformation from './BreakInformation';
import TherapeuticApplications from './TherapeuticApplications';
import { useAnalytics } from '../../hooks/useAnalytics';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import UpgradeModal from '../UpgradeModal';
import { useSubscription } from '../../hooks/useSubscription';

const Calculations = () => {
  const { user } = useAuth();
  const { isActive: isSubActive } = useSubscription();
  const [upgradeModal, setUpgradeModal] = useState({ open: false });
  const [phoneNeeded, setPhoneNeeded] = useState(false);
  const initialLoadRef = useRef(false);
  const raceInfoRef = useRef(null);
  const { trackUserAction, trackPageView } = useAnalytics();

  const [formData, setFormData] = useState({
    // Race Information
    raceName: '',
    raceDate: '',
    raceStartTime: '',
    estRunsPerHour: '',
    raceTimezone: '',

    // Horse Information
    horseName: '',
    riderName: '',
    className: '',
    draw: '',

    // Break Information
    breakDrawNumber: '',
    breakDuration: '',

    // Therapeutic Times
    hoursBeforeRun1: '',
    hoursBeforeRun2: '',
    hoursBeforeRun3: '',

    // Calculation Fields
    totalRuns: '',
    averageRunTime: '',
    isReverseOrder: false
  });

  const [results, setResults] = useState(null);
  const [hasCalculated, setHasCalculated] = useState(false);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [savedHorses, setSavedHorses] = useState([]);
  const [editingHorseId, setEditingHorseId] = useState(null);
  const [classes, setClasses] = useState([]);
  const [marginTop, setMarginTop] = useState('0px');
  const [deleteHorseConfirm, setDeleteHorseConfirm] = useState({
    open: false,
    horseId: null,
  });


  // Flow 1 (01/19/26): after calculating times, ask if user wants a one-time summary text (only if globally opted-in).
  const [summarySmsPrompt, setSummarySmsPrompt] = useState({
    open: false,
    sending: false,
    horse: null, // { horse_name, time_to_give_tool1, time_to_give_tool2, time_to_give_tool3, estimated_run_time }
  });

  // Flow 2: after saving a horse, ask if user wants ongoing text reminders for that horse.
  const [horseReminderPrompt, setHorseReminderPrompt] = useState({
    open: false,
    horseId: null,
    needsPhoneNumber: false, // Flag to show different message if phone number is missing
    raceInPast: false, // Flag to show message if race date is in the past
  });

  // Per-horse master reminder toggle (UI-only for now).
  // Example shape: { [horseId]: true | false }
  const [horseReminderEnabledMap, setHorseReminderEnabledMap] = useState({});

  const colors = {
    primary: '#29458d',
    primaryDark: '#1e3266',
    secondary: '#dbc078',
    background: '#f8fafc',
    surface: '#ffffff',
    border: '#e2e8f0',
    text: {
      primary: '#1a202c',
      secondary: '#4a5568',
      hint: '#718096'
    },
    tool1: '#2563eb',
    tool2: '#7c3aed',
    tool3: '#db2777',
    danger: '#ef4444',
    error: '#ef4444',
    warning: '#f59e0b',
    info: '#3b82f6'
  };

  const commonStyles = {
    section: {
      backgroundColor: colors.surface,
      padding: '24px',
      borderRadius: '12px',
      marginBottom: '24px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      '& h3': {
        color: colors.text.primary,
        fontSize: '1.2rem',
        fontWeight: '600',
        marginBottom: '20px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        paddingBottom: '12px',
        borderBottom: `2px solid ${colors.primary}`
      }
    },
    input: {
      width: '100%',
      padding: '12px 16px',
      border: `1px solid ${colors.border}`,
      borderRadius: '8px',
      fontSize: '16px',
      backgroundColor: colors.surface,
      transition: 'all 0.2s ease',
      outline: 'none',
      '&:focus': {
        borderColor: colors.primary,
        boxShadow: `0 0 0 3px ${colors.primary}25`
      }
    }
  };

  const getFieldError = useCallback((fieldName) => {
    if (!fieldName) return null;

    switch (fieldName) {
      case 'horseName':
        if (!formData.horseName?.trim()) {
          return 'Horse name is required';
        }
        break;
      case 'raceDate':
        if (!formData.raceDate) {
          return 'Race date is required';
        }
        break;
      case 'draw':
        if (!formData.draw || parseInt(formData.draw, 10) < 1) {
          return 'Valid draw number is required';
        }
        break;
      case 'raceStartTime':
        if (!formData.raceStartTime) {
          return 'Race start time is required';
        }
        break;
      case 'estRunsPerHour':
        if (!formData.estRunsPerHour || parseInt(formData.estRunsPerHour, 10) < 1) {
          return 'Valid estimated runs per hour is required';
        }
        break;
      case 'raceTimezone':
        if (!formData.raceTimezone) {
          return 'Race timezone is required';
        }
        break;
      case 'totalRuns':
        if (formData.isReverseOrder && (!formData.totalRuns || parseInt(formData.totalRuns, 10) < 1)) {
          return 'Total runners is required when using reverse order';
        }
        break;
      default:
        break;
    }

    return null;
  }, [formData]);

  const validateField = useCallback((fieldName) => {
    const message = getFieldError(fieldName);
    if (message) {
      setError(message);
      return false;
    }
    setError('');
    return true;
  }, [getFieldError]);

  const handleInputChange = useCallback((e) => {
    if (!e?.target) return;

    const { name, value, type, checked } = e.target;
    if (!name) return;

    try {
      let newValue = type === 'checkbox' ? checked : value;

      if (name === 'raceDate' && typeof newValue === 'string') {
        const parts = newValue.split('-');

        if (parts.length === 3) {
          const yearIndex = parts.findIndex((p) => p.length >= 4);
          if (yearIndex !== -1) {
            parts[yearIndex] = parts[yearIndex].slice(0, 4);
            newValue = parts.join('-');
          }
        }
      }

      setFormData(prevData => ({
        ...prevData,
        [name]: newValue
      }));

      // Any change invalidates the last calculation
      setHasCalculated(false);

      // Validate totalRuns when reverse order is checked
      if (name === 'isReverseOrder' && checked) {
        validateField('totalRuns');
      }

      if (error) setError('');
    } catch (err) {
      console.error('Error in handleInputChange:', err);
      setError('Error updating form data');
    }
  }, [error, validateField]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields and collect all error messages
    const requiredFields = ['horseName', 'raceDate', 'raceStartTime', 'estRunsPerHour', 'raceTimezone', 'draw'];
    const errors = [];

    requiredFields.forEach((field) => {
      const message = getFieldError(field);
      if (message) {
        errors.push(message);
      }
    });

    // Validate totalRuns if reverse order is checked
    const totalRunsError = getFieldError('totalRuns');
    if (totalRunsError) {
      errors.push(totalRunsError);
    }

    if (errors.length > 0) {
      // Join multiple validation messages into a single, user-friendly string.
      // Example: "Horse name is required • Race start time is required • ..."
      setError(errors.join(' • '));
      return;
    }

    setError('');

    try {
      // Parse the race start time
      const [hours, minutes] = formData.raceStartTime.split(':');
      const raceStartTime = new Date();
      raceStartTime.setHours(parseInt(hours, 10));
      raceStartTime.setMinutes(parseInt(minutes, 10));
      raceStartTime.setSeconds(0);
      raceStartTime.setMilliseconds(0);

      // Calculate total running position including previous classes
      const draw = parseInt(formData.draw, 10) || 0;
      const previousClassesTotal = classes.reduce((total, c) => total + (parseInt(c.runs) || 0), 0);
      const totalRuns = parseInt(formData.totalRuns) || 0;
      const totalRunningPosition = formData.isReverseOrder && totalRuns
        ? (totalRuns - draw) + previousClassesTotal
        : draw + previousClassesTotal;

      // Calculate minutes per run based on runs per hour
      const runsPerHour = parseInt(formData.estRunsPerHour, 10) || 0;
      const minutesPerRun = runsPerHour > 0 ? 60 / runsPerHour : 0;

      // Calculate total minutes until run
      let totalMinutesUntilRun = 0;
      const breakDrawNum = parseInt(formData.breakDrawNumber, 10) || 0;
      const breakDurationHours = parseFloat(formData.breakDuration) || 0;

      // If there's a break and we're after it
      if (breakDrawNum && totalRunningPosition > breakDrawNum) {
        // Calculate minutes until break
        const minutesUntilBreak = breakDrawNum * minutesPerRun;

        // Calculate remaining runs after break
        const remainingRuns = totalRunningPosition - breakDrawNum;
        const minutesAfterBreak = remainingRuns * minutesPerRun;

        // Total time is sum of: time until break + break duration + time after break
        totalMinutesUntilRun = minutesUntilBreak + (breakDurationHours * 60) + minutesAfterBreak;
      } else {
        // Simple calculation if before break or no break
        totalMinutesUntilRun = totalRunningPosition * minutesPerRun;
      }

      // Add the calculated minutes to race start time
      const estimatedRunTime = new Date(raceStartTime);
      estimatedRunTime.setMinutes(estimatedRunTime.getMinutes() + Math.floor(totalMinutesUntilRun));

      // Format the estimated run time
      const formattedEstimatedRunTime = estimatedRunTime.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });

      // Calculate therapeutic times
      const calculateToolTime = (hoursBeforeRun) => {
        if (!hoursBeforeRun) return '-';
        const toolTime = new Date(estimatedRunTime);
        const hours = parseFloat(hoursBeforeRun);
        toolTime.setHours(toolTime.getHours() - Math.floor(hours));
        toolTime.setMinutes(toolTime.getMinutes() - ((hours % 1) * 60));
        return toolTime.toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit',
          hour12: true
        });
      };

      // Set the results
      const nextResults = {
        estimatedRunTime: formattedEstimatedRunTime,
        totalRunningPosition,
        minutesPerRun,
        timeToGiveTool1: calculateToolTime(formData.hoursBeforeRun1),
        timeToGiveTool2: calculateToolTime(formData.hoursBeforeRun2),
        timeToGiveTool3: calculateToolTime(formData.hoursBeforeRun3)
      };
      setResults(nextResults);
      setHasCalculated(true);

      // Clear any previous errors
      setError('');

      trackUserAction('calculation_submitted', {
        calculationType: 'performance_go',
        // Add other relevant data
      });

      // Flow: after calculating times, ask if user wants a summary text.
      // Only show if (a) consent is enabled, (b) they have an active phone number, (c) race date is not in the past, and (d) not STOP opted-out.
      // We send a *horse payload* (not horseId) to avoid per-horse sms_enabled gating for this explicit one-time send.
      try {
        const { data: profileNow, error: profileErr } = await supabase
          .from('profiles')
          .select('sms_opt_in, phone_number, sms_opt_out_source')
          .eq('user_id', user.id)
          .maybeSingle();
        if (profileErr) throw profileErr;

        const optedInNow = !!profileNow?.sms_opt_in;
        const hasPhoneNow = !!profileNow?.phone_number;
        const isStopOptedOut = profileNow?.sms_opt_out_source === 'stop' && !optedInNow;

        // Don't show popup if STOP opt-out
        if (isStopOptedOut) {
          // Silently don't show popup
          return;
        }

        // Check if race date is in the past
        const isRaceDateInPast = (() => {
          if (!formData.raceDate) return false;
          try {
            // Normalize to YYYY-MM-DD if needed (handle MM/DD/YYYY)
            let dateStr = formData.raceDate;
            if (formData.raceDate.includes('/')) {
              const [month, day, year] = formData.raceDate.split('/');
              dateStr = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
            }

            const [yearStr, monthStr, dayStr] = dateStr.split('-');
            const year = Number(yearStr);
            const month = Number(monthStr);
            const day = Number(dayStr);

            if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
              return false;
            }

            // Race date at local midnight
            const raceDateOnly = new Date(year, month - 1, day);
            // Today's date at local midnight
            const now = new Date();
            const todayDateOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            // Consider race "past" only if its calendar date is before today
            return raceDateOnly < todayDateOnly;
          } catch (err) {
            console.error('Error checking if race date is in past:', err);
            return false;
          }
        })();

        // Non-subscribed user: suppress SMS-related prompts; race is free, upgrade gates handle paid features
        if (!isSubActive && !isRaceDateInPast) {
          // Skip SMS prompts entirely for free users — avoids confusing them into adding a phone
          // that can't be used. The UpgradeModal triggers when they actively try a paid action.
        } else if (isSubActive && optedInNow && hasPhoneNow && !isRaceDateInPast) {
          // Only show popup if race date is NOT in the past
          setSummarySmsPrompt({
            open: true,
            sending: false,
            horse: {
              horse_name: formData.horseName || 'Your horse',
              time_to_give_tool1: nextResults.timeToGiveTool1 !== '-' ? nextResults.timeToGiveTool1 : null,
              time_to_give_tool2: nextResults.timeToGiveTool2 !== '-' ? nextResults.timeToGiveTool2 : null,
              time_to_give_tool3: nextResults.timeToGiveTool3 !== '-' ? nextResults.timeToGiveTool3 : null,
              estimated_run_time: nextResults.estimatedRunTime || null,
              draw_number: formData.draw ? parseInt(formData.draw, 10) : null,
              race_date: formData.raceDate || null,
            },
          });
        } else if (isSubActive && !hasPhoneNow) {
          // Subscribed user hasn't added phone yet
          setSnackbar({
            open: true,
            message: 'Add a phone number in Profile Settings to receive text summaries of your calculated times.',
            severity: 'warning'
          });
        } else if (isSubActive && !optedInNow) {
          // Subscribed user has phone but SMS is disabled
          setSnackbar({
            open: true,
            message: 'Enable SMS reminders in Profile Settings to receive text summaries of your calculated times.',
            severity: 'warning'
          });
        }
        // Note: If race date is in past, silently don't show popup (no snackbar message needed)
      } catch (e) {
        // Non-blocking: calculation succeeded; we just won't prompt for SMS if profile read fails.
        console.warn('Failed to check SMS eligibility after calculation (non-blocking):', e);
      }

    } catch (error) {
      console.error('Calculation error:', error);
      setError('Error calculating times: ' + error.message);
    }
  };

  const fetchSavedHorses = useCallback(async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('consolidated_horses')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setSavedHorses(data || []);

      // CRITICAL FIX: Check if sms_enabled is true but no reminders exist
      // If so, set sms_enabled to false to match the actual state
      const horseIds = (data || []).map(h => h.id);
      if (horseIds.length > 0) {
        // Check for any active reminders for these horses
        const { data: reminders } = await supabase
          .from('scheduled_reminders')
          .select('horse_id, status')
          .eq('user_id', user.id)
          .in('horse_id', horseIds)
          .in('status', ['pending', 'processing']);

        // Group reminders by horse_id
        const horsesWithReminders = new Set();
        if (reminders) {
          reminders.forEach(r => {
            if (r.status === 'pending' || r.status === 'processing') {
              horsesWithReminders.add(r.horse_id);
            }
          });
        }

        // Fix any horses where sms_enabled=true but no reminders exist
        const horsesToFix = (data || []).filter(horse =>
          horse.sms_enabled === true && !horsesWithReminders.has(horse.id)
        );

        if (horsesToFix.length > 0) {
          const horseIdsToFix = horsesToFix.map(h => h.id);
          await supabase
            .from('consolidated_horses')
            .update({
              sms_enabled: false,
              sms_disabled_at: new Date().toISOString()
            })
            .in('id', horseIdsToFix)
            .eq('user_id', user.id);

          // Update the data array to reflect the fix
          horsesToFix.forEach(horse => {
            horse.sms_enabled = false;
          });
        }
      }

      // Always read from database to ensure state matches DB on refresh
      // On initial load (refresh), state is empty, so we read from DB
      // During session, this preserves any unsaved changes in state
      setHorseReminderEnabledMap((prev) => {
        const next = { ...prev };
        const isInitialLoad = Object.keys(prev).length === 0;

        (data || []).forEach(horse => {
          if (isInitialLoad) {
            // On initial load (refresh), always read from database
            // Explicitly check for false to ensure toggled-off state is preserved
            // CRITICAL: If no reminders exist, sms_enabled should be false
            next[horse.id] = horse.sms_enabled === false ? false : true;
          } else {
            // During session, only initialize if not already set (preserves user's current toggle state)
            if (next[horse.id] === undefined) {
              // Explicitly check for false to ensure toggled-off state is preserved
              next[horse.id] = horse.sms_enabled === false ? false : true;
            }
          }
        });
        return next;
      });
    } catch (error) {
      console.error('Error fetching saved horses:', error);
      setError('Failed to fetch saved horses');
    }
  }, [user]);

  // Single useEffect for initial load
  useEffect(() => {
    if (!user || initialLoadRef.current) return;

    initialLoadRef.current = true;
    fetchSavedHorses();
  }, [user, fetchSavedHorses]);


  useEffect(() => {
    trackPageView('calculations');
  }, [trackPageView]);

  useEffect(() => {
    if (!results) return;
    const element = document.getElementById('calculated-times-section');
    if (!element) return;
    const headerOffset = document.querySelector('.top-nav')?.offsetHeight || 80;
    const offsetPosition = element.getBoundingClientRect().top + window.scrollY - headerOffset;
    window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
  }, [results]);

  // Ensure the user's timezone is saved to profile early. The backend's "is race past?" check
  // reads profile.timezone; without it, the function falls back to UTC and can incorrectly
  // reject same-day sends made late-evening in US timezones. Auto-save here covers users who
  // haven't yet rendered SavedHorses (where the same auto-save also exists).
  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const { data: profile } = await supabase
          .from('profiles')
          .select('timezone')
          .eq('user_id', user.id)
          .maybeSingle();
        if (cancelled) return;
        const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (!browserTz || profile?.timezone === browserTz) return;
        await supabase.from('profiles').update({ timezone: browserTz }).eq('user_id', user.id);
      } catch (err) {
        // Non-blocking — backend will fall back to UTC if this fails.
        console.warn('Failed to ensure user timezone (non-blocking):', err);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  const saveHorse = async (e) => {
    // Prevent any default form submission behavior
    if (e) {
      e.preventDefault();
    }

    try {
      if (!user) {
        setError('Please sign in to save horses');
        return;
      }

      // Check if times have been calculated
      if (!results || !results.estimatedRunTime) {
        setError('Please calculate times before saving');
        return;
      }

  

      // Create the base horse data object
      const horseData = {
        user_id: user.id,
        race_name: formData.raceName || '',
        race_date: formData.raceDate || null,
        rider_name: formData.riderName || '',
        horse_name: formData.horseName,
        class_name: formData.className || '',
        race_start_time: formData.raceStartTime,
        est_runs_per_hour: parseInt(formData.estRunsPerHour),
        race_timezone: formData.raceTimezone || null,
        draw_number: parseInt(formData.draw),
        break_draw_number: formData.breakDrawNumber ? parseInt(formData.breakDrawNumber) : null,
        break_duration: formData.breakDuration ? parseFloat(formData.breakDuration) : null,
        hours_before_run1: formData.hoursBeforeRun1 ? parseFloat(formData.hoursBeforeRun1) : null,
        hours_before_run2: formData.hoursBeforeRun2 ? parseFloat(formData.hoursBeforeRun2) : null,
        hours_before_run3: formData.hoursBeforeRun3 ? parseFloat(formData.hoursBeforeRun3) : null,
        previous_classes: classes,
        estimated_run_time: results.estimatedRunTime,
        time_to_give_tool1: results.timeToGiveTool1 !== '-' ? results.timeToGiveTool1 : null,
        time_to_give_tool2: results.timeToGiveTool2 !== '-' ? results.timeToGiveTool2 : null,
        time_to_give_tool3: results.timeToGiveTool3 !== '-' ? results.timeToGiveTool3 : null,
        total_runs: formData.totalRuns ? parseInt(formData.totalRuns) : null,
        reverse_order: formData.isReverseOrder
      };


      let response;
      if (editingHorseId) {

        // CRITICAL FIX: Preserve sms_enabled when editing - fetch current value first
        let currentSmsEnabled = true; // Default to true
        try {
          const { data: currentHorse } = await supabase
            .from('consolidated_horses')
            .select('sms_enabled')
            .eq('id', editingHorseId)
            .eq('user_id', user.id)
            .single();

          if (currentHorse?.sms_enabled !== undefined) {
            currentSmsEnabled = currentHorse.sms_enabled !== false;
          }
        } catch (err) {
          console.warn('Could not fetch current sms_enabled, using default:', err);
        }

        // Preserve sms_enabled in the update
        const updateData = {
          ...horseData,
          sms_enabled: currentSmsEnabled
        };

        response = await supabase
          .from('consolidated_horses')
          .update(updateData)
          .eq('id', editingHorseId)
          .select('*')
          .single();
      } else {
        response = await supabase
          .from('consolidated_horses')
          .insert([horseData])
          .select('*')
          .single();
      }


      if (response.error) {
        console.error('Supabase error:', response.error);
        throw response.error;
      }

      const savedHorseRow = response.data;

      // Fetch fresh data after successful save
      await fetchSavedHorses();

      if (editingHorseId) {
        setEditingHorseId(null);
      }

      // Clear form data after successful save
      setFormData({
        raceName: '',
        raceDate: '',
        raceStartTime: '',
        estRunsPerHour: '',
        raceTimezone: '',
        horseName: '',
        riderName: '',
        className: '',
        draw: '',
        totalRuns: '',
        isReverseOrder: false
      });

      // Clear classes
      setClasses([]);

      // Clear calculated times/results
      setResults(null);
      setHasCalculated(false);

      setError('Horse saved successfully!');

      // After saving, check race date and phone number before asking about reminders
      if (savedHorseRow?.id) {
        try {

          const raceDateStr = savedHorseRow.race_date;
          let isRaceInPast = false;

          if (raceDateStr) {
            try {
              // raceDateStr is expected to be in YYYY-MM-DD format
              const [yearStr, monthStr, dayStr] = raceDateStr.split('-');
              const year = Number(yearStr);
              const month = Number(monthStr);
              const day = Number(dayStr);

              if (Number.isFinite(year) && Number.isFinite(month) && Number.isFinite(day)) {
                // Build local dates at midnight for both race date and "today"
                const raceDateOnly = new Date(year, month - 1, day);
                const now = new Date();
                const todayDateOnly = new Date(
                  now.getFullYear(),
                  now.getMonth(),
                  now.getDate()
                );

                // Race is "past" only if its date is strictly before today
                isRaceInPast = raceDateOnly < todayDateOnly;
              }
            } catch (err) {
              console.warn('Error checking if race is in past (date-only):', err);
            }
          }

          // Check if therapeutic times are set (at least one must be set for reminders)
          const hasTherapeuticTimes = (
            (savedHorseRow.time_to_give_tool1 && savedHorseRow.time_to_give_tool1 !== '-') ||
            (savedHorseRow.time_to_give_tool2 && savedHorseRow.time_to_give_tool2 !== '-') ||
            (savedHorseRow.time_to_give_tool3 && savedHorseRow.time_to_give_tool3 !== '-')
          );

          // Only show reminder prompt if therapeutic times are set
          if (!hasTherapeuticTimes) {
            // Don't show prompt if no therapeutic times are set
          } else if (isRaceInPast) {
            // If race is in past, show message about that
            setHorseReminderPrompt({
              open: true,
              horseId: savedHorseRow.id,
              needsPhoneNumber: false,
              raceInPast: true,
            });
          } else if (!isSubActive) {
            // Free user — show paywall. Phone is collected later, only if they click "Start Free Trial".
            // Suppress paywall for 5 days after dismissal so it doesn't fire on every save.
            const dismissedAt = Number(localStorage.getItem('pgp_save_paywall_dismissed_at') || 0);
            const fiveDaysMs = 5 * 24 * 60 * 60 * 1000;
            if (Date.now() - dismissedAt >= fiveDaysMs) {
              setUpgradeModal({ open: true, source: 'save_horse' });
            }
          } else {
            // Race is not in past, check phone number and SMS status
            const { data: profile, error: profileError } = await supabase
              .from('profiles')
              .select('phone_number, sms_opt_in, sms_opt_out_source')
              .eq('user_id', user.id)
              .single();

            if (profileError || !profile) {
              console.warn('Failed to check profile for reminder prompt');
              // Don't show prompt if we can't verify
            } else {
              // Check for STOP opt-out - don't show prompt if STOP opted-out
              const isStopOptedOut = profile.sms_opt_out_source === 'stop' && !profile.sms_opt_in;
              if (isStopOptedOut) {
                // Don't show prompt if STOP opted-out - user can save without SMS setup
                // Continue normally (don't return early)
              } else if (profile.sms_opt_in) {
                // Only show prompt if SMS is enabled
                // If SMS is disabled (but not STOP), user can save without being prompted
                // Check if phone number exists
                if (!profile.phone_number) {
                  // Show prompt with message to add phone number first
                  setHorseReminderPrompt({
                    open: true,
                    horseId: savedHorseRow.id,
                    needsPhoneNumber: true, // Flag to show different message
                    raceInPast: false,
                  });
                } else {
                  // Phone number exists and SMS is enabled, show normal reminder prompt
                  setHorseReminderPrompt({
                    open: true,
                    horseId: savedHorseRow.id,
                    needsPhoneNumber: false,
                    raceInPast: false,
                  });
                }
              }
              // If sms_opt_in is false (and not STOP), don't show any prompt - user can save without SMS setup
            }
          }
        } catch (err) {
          console.error('Error checking phone number for reminder prompt:', err);
          // Don't show prompt on error
        }
      }

      // Clear error message after 3 seconds
      setTimeout(() => {
        setError('');
      }, 3000);

    } catch (error) {
      console.error('Error saving horse:', error);
      // Network / fetch failures (WebKit "Load failed", Chrome "Failed to fetch", etc.) → friendlier message
      const isNetworkError =
        error?.name === 'TypeError' &&
        /load failed|failed to fetch|network/i.test(error?.message || '');
      if (isNetworkError) {
        setError(
          "Connection issue — your horse wasn't saved. Please check your internet and tap Save Horse again. Your details are still here."
        );
      } else {
        setError(`Failed to save horse: ${error.message || 'Please try again.'}`);
      }
    }
  };

  const onEditHorse = useCallback((horse) => {
    if (!horse?.id) return;

    try {
      setEditingHorseId(horse.id);

      // Set the stored classes array directly
      setClasses(Array.isArray(horse.previous_classes) ? horse.previous_classes : []);

      setFormData({
        raceName: horse.race_name || '',
        raceDate: horse.race_date || '',
        raceStartTime: horse.race_start_time || '',
        estRunsPerHour: horse.est_runs_per_hour?.toString() || '',
        raceTimezone: horse.race_timezone || '',
        horseName: horse.horse_name || '',
        riderName: horse.rider_name || '',
        className: horse.class_name || '',
        draw: horse.draw_number?.toString() || '',
        breakDrawNumber: horse.break_draw_number?.toString() || '',
        breakDuration: horse.break_duration?.toString() || '',
        hoursBeforeRun1: horse.hours_before_run1?.toString() || '',
        hoursBeforeRun2: horse.hours_before_run2?.toString() || '',
        hoursBeforeRun3: horse.hours_before_run3?.toString() || '',
        totalRuns: horse.total_runs?.toString() || '',
        isReverseOrder: horse.reverse_order || false
      });

      // Scroll to Race Info section after a short delay to ensure state updates are complete
      setTimeout(() => {
        raceInfoRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);

      setError('');
    } catch (err) {
      console.error('Error in onEditHorse:', err);
      setError('Error loading horse data for editing');
    }
  }, []);

  const cloneHorse = async (horseToClone) => {
    try {
      // Create new horse object with copied data
      const newHorse = {
        user_id: user.id,
        race_name: horseToClone.race_name,
        race_date: horseToClone.race_date,
        rider_name: horseToClone.rider_name,
        horse_name: `${horseToClone.horse_name} (Copy)`,
        class_name: horseToClone.class_name,
        race_start_time: horseToClone.race_start_time,
        est_runs_per_hour: horseToClone.est_runs_per_hour,
        race_timezone: horseToClone.race_timezone,
        draw_number: horseToClone.draw_number,
        break_draw_number: horseToClone.break_draw_number,
        break_duration: horseToClone.break_duration,
        hours_before_run1: horseToClone.hours_before_run1,
        hours_before_run2: horseToClone.hours_before_run2,
        hours_before_run3: horseToClone.hours_before_run3,
        time_to_give_tool1: horseToClone.time_to_give_tool1,
        time_to_give_tool2: horseToClone.time_to_give_tool2,
        time_to_give_tool3: horseToClone.time_to_give_tool3,
        estimated_run_time: horseToClone.estimated_run_time,
        previous_classes: horseToClone.previous_classes,
        total_runs: horseToClone.total_runs,
        reverse_order: horseToClone.reverse_order
      };

      // Insert the new horse into Supabase
      const { error } = await supabase
        .from('consolidated_horses')
        .insert([newHorse]);

      if (error) throw error;

      // Fetch fresh data after successful insert
      await fetchSavedHorses();

      setError('Horse cloned successfully!');

      // Clear error message after 3 seconds
      setTimeout(() => {
        setError('');
      }, 3000);

    } catch (error) {
      console.error('Error cloning horse:', error);
      setError('Failed to clone horse. Please try again.');
    }
  };

  const sendHorseSummarySms = useCallback(
    async (horse) => {
      if (!horse?.id) {
        setError('Missing horse information for summary text.');
        setTimeout(() => setError(''), 3000);
        return;
      }

      if (!isSubActive) {
        // Check if user has a phone number before showing upgrade modal
        const { data: prof } = await supabase.from('profiles').select('phone_number').eq('user_id', user.id).single();
        if (!prof?.phone_number) {
          setPhoneNeeded(true);
          return;
        }
        setUpgradeModal({ open: true });
        return;
      }

      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const accessToken = sessionData?.session?.access_token;
        if (!accessToken) throw new Error('Missing session access token');

        const supabaseUrl = (process.env.REACT_APP_SUPABASE_URL || '').replace(/\/+$/, '');
        const anonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || '';
        if (!supabaseUrl || !anonKey) throw new Error('Missing Supabase env vars');

        const resp = await fetch(`${supabaseUrl}/functions/v1/send-horse-summary-sms`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            apikey: anonKey,
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ horseId: horse.id }),
        });

        if (!resp.ok) {
          const rawBody = await resp.text().catch(() => '');
          console.warn('Saved-horse summary SMS failed:', { status: resp.status, statusText: resp.statusText, body: rawBody });
          let errData = {};
          try { errData = JSON.parse(rawBody) || {}; } catch (_) { }
          const errMsg = (errData.error || rawBody || '').toLowerCase();
          let userMsg = 'Failed to send text. Please check your SMS settings in Profile.';
          if (resp.status === 402 || errMsg.includes('subscription_required')) {
            setUpgradeModal({ open: true });
            return;
          }
          if (resp.status === 409) {
            if (errMsg.includes('opted out') || errMsg.includes('stop') || errMsg.includes('reply start')) {
              userMsg = "You previously unsubscribed by texting STOP. To get texts again, text START to the Pre Go Prep number.";
            } else if (errMsg.includes('not opted in')) {
              userMsg = 'SMS is turned off in your profile. Turn on SMS in Profile → SMS Notifications to receive texts.';
            } else if (errMsg.includes('sms disabled for this horse')) {
              userMsg = 'SMS reminders are disabled for this horse. Enable reminders using the toggle on the horse card.';
            }
          } else if (resp.status === 403 && errMsg.includes('allowlist')) {
            userMsg = "Texts aren't enabled for this number right now.";
          } else if (resp.status === 400 && errMsg.includes('phone')) {
            userMsg = 'Add a phone number in Profile Settings to receive text summaries.';
          } else if (resp.status === 400 && errMsg.includes('past race')) {
            userMsg = 'This race date is in the past. Race-day summary texts can only be sent for upcoming or same-day races.';
          } else if (resp.status === 401) {
            userMsg = 'Please sign in again and try again.';
          }
          setSnackbar({ open: true, message: userMsg, severity: 'error' });
          setError(userMsg);
          setTimeout(() => setError(''), 5000);
          return;
        }

        setSnackbar({ open: true, message: 'Summary text sent successfully!', severity: 'success' });
        setError('Text sent successfully!');
        setTimeout(() => setError(''), 3000);
      } catch (e) {
        console.warn('Saved-horse summary SMS error:', e);
        const userMsg = 'Failed to send text. Please check your SMS settings in Profile.';
        setSnackbar({ open: true, message: userMsg, severity: 'error' });
        setError(userMsg);
        setTimeout(() => setError(''), 5000);
      }
    },
    [isSubActive, user?.id]
  );

  const handleConfirmDeleteHorse = useCallback(
    async () => {
      const id = deleteHorseConfirm.horseId;
      if (!id) {
        setDeleteHorseConfirm({ open: false, horseId: null });
        return;
      }

      try {
        const { error: deleteError } = await supabase
          .from('consolidated_horses')
          .delete()
          .eq('id', id);

        if (deleteError) throw deleteError;

        setSavedHorses((prev) => prev.filter((h) => h.id !== id));
        setError('Horse deleted successfully!');
      } catch (err) {
        console.error('Error deleting horse:', err);
        setError('Error deleting horse');
      } finally {
        setDeleteHorseConfirm({ open: false, horseId: null });
        setTimeout(() => setError(''), 3000);
      }
    },
    [deleteHorseConfirm.horseId]
  );

  useEffect(() => {
    const handleResize = () => {
      setMarginTop(window.innerWidth <= 768 ? '0px' : '0px');
    };

    // Set initial value
    handleResize();

    // Add event listener
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div style={{
      maxWidth: '1200px',
      margin: `${marginTop} auto 0`,
      padding: '0 20px',
      position: 'relative'
    }}>
      {/* Title and How to Use Section */}
      <h1 style={{
        fontSize: '2.5rem',
        fontWeight: '700',
        color: colors.primaryDark,
        textAlign: 'center',
        marginTop: '1rem',
        marginBottom: '2rem'
      }}>
        Pre Go Prep Calculator
      </h1>

      <div style={{
        ...commonStyles.section,
        marginBottom: '2rem'
      }}>
        <h2 style={{
          fontSize: '1.5rem',
          fontWeight: '600',
          color: colors.text.primary,
          marginBottom: '1rem',
          textAlign: 'center'
        }}>
          How to Use This Calculator
        </h2>

        <p style={{
          color: colors.text.secondary,
          textAlign: 'center',
          marginBottom: '2rem',
          fontSize: '1rem',
          lineHeight: '1.5',
          maxWidth: '800px',
          margin: '0 auto 2rem'
        }}>
          Use this calculator to drive efficiency and precision in your performance Go! Enter your race details,
          horse information, and therapeutic tool timings to get an accurate schedule for your race day.
        </p>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '1.5rem',
          maxWidth: '1000px',
          margin: '0 auto',
          justifyContent: 'center'
        }}>
          <div style={{
            backgroundColor: colors.background,
            padding: '1.5rem',
            borderRadius: '8px',
            border: `1px solid ${colors.border}`,
            height: '100%',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              marginBottom: '0.5rem'
            }}>
              <span style={{
                backgroundColor: colors.primary,
                color: 'white',
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: '600'
              }}>1</span>
              <h3 style={{
                fontSize: '1.1rem',
                fontWeight: '600',
                color: colors.text.primary
              }}>Enter Race Details</h3>
            </div>
            <p style={{
              color: colors.text.secondary,
              fontSize: '0.9rem'
            }}>
              Start by entering your race information, including start time and estimated runs per hour. This helps calculate accurate timing for your performance.
            </p>
          </div>

          <div style={{
            backgroundColor: colors.background,
            padding: '1.5rem',
            borderRadius: '8px',
            border: `1px solid ${colors.border}`,
            height: '100%',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              marginBottom: '0.5rem'
            }}>
              <span style={{
                backgroundColor: colors.primary,
                color: 'white',
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: '600'
              }}>2</span>
              <h3 style={{
                fontSize: '1.1rem',
                fontWeight: '600',
                color: colors.text.primary
              }}>Add Horse Information</h3>
            </div>
            <p style={{
              color: colors.text.secondary,
              fontSize: '0.9rem'
            }}>
              Input your horse's details and draw number. If there are classes running before yours, add them to calculate your actual running position.
            </p>
          </div>

          <div style={{
            backgroundColor: colors.background,
            padding: '1.5rem',
            borderRadius: '8px',
            border: `1px solid ${colors.border}`,
            height: '100%',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              marginBottom: '0.5rem'
            }}>
              <span style={{
                backgroundColor: colors.primary,
                color: 'white',
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: '600'
              }}>3</span>
              <h3 style={{
                fontSize: '1.1rem',
                fontWeight: '600',
                color: colors.text.primary
              }}>Set Therapeutic Times</h3>
            </div>
            <p style={{
              color: colors.text.secondary,
              fontSize: '0.9rem'
            }}>
              Specify when you need to apply therapeutic tools before your run. Enter times in hours (e.g., 0.25 for 15 minutes, 1.5 for 1 hour 30 minutes).
            </p>
          </div>
        </div>
      </div>

      {/* Race Information Section */}
      <div ref={raceInfoRef}>
        <RaceInformation
          formData={formData}
          handleInputChange={handleInputChange}
          validateField={validateField}
          commonStyles={commonStyles}
          colors={colors}
        />
      </div>

      <HorseInformation
        formData={formData}
        handleInputChange={handleInputChange}
        validateField={validateField}
        commonStyles={commonStyles}
        classes={classes}
        setClasses={setClasses}
        colors={colors}
      />

      <BreakInformation
        formData={formData}
        handleInputChange={handleInputChange}
        commonStyles={commonStyles}
        colors={colors}
      />

      <TherapeuticApplications
        formData={formData}
        handleInputChange={handleInputChange}
        commonStyles={commonStyles}
        colors={colors}
      />

      <Form
        formData={formData}
        results={results}
        handleSubmit={handleSubmit}
        handleInputChange={handleInputChange}
        error={error}
        colors={colors}
        commonStyles={commonStyles}
      />

      {/* Save and Clear Buttons */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '16px',
        marginTop: '40px',
        marginBottom: '40px'
      }}>
        {/* Anchor wrapper so prompts are always directly above the Save button */}
        <div style={{ position: 'relative', width: '100%', maxWidth: '300px' }}>
          {/* Flow 1: One-time summary SMS prompt after Calculate Times */}
          {summarySmsPrompt.open && (
            <div
              style={{
                position: 'absolute',
                left: '50%',
                transform: 'translateX(-50%)',
                bottom: 'calc(100% + 6px)',
                zIndex: 50,
                width: 'min(680px, calc(100vw - 32px))',
                backgroundColor: colors.surface,
                border: `1px solid ${colors.border}`,
                borderRadius: '12px',
                boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
                padding: '18px 18px',
                minHeight: '110px',
              }}
              role="dialog"
              aria-label="Send schedule via text"
            >
              <div style={{ fontWeight: 700, color: colors.text.primary, marginBottom: '6px' }}>
                Would you like a text message summary of this horse sent to your phone?
              </div>

              <div
                style={{
                  display: 'flex',
                  gap: '10px',
                  marginTop: '12px',
                  justifyContent: 'flex-end',
                }}
              >
                <button
                  type="button"
                  disabled={summarySmsPrompt.sending}
                  onClick={async () => {
                    try {
                      setSummarySmsPrompt((p) => ({ ...p, sending: true }));

                      // Use a direct fetch so we can control headers precisely.
                      const { data: sessionData } = await supabase.auth.getSession();
                      const accessToken = sessionData?.session?.access_token;
                      if (!accessToken) throw new Error('Missing session access token');

                      const supabaseUrl = (process.env.REACT_APP_SUPABASE_URL || '').replace(/\/+$/, '');
                      const anonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || '';
                      if (!supabaseUrl || !anonKey) throw new Error('Missing Supabase env vars');

                      const resp = await fetch(`${supabaseUrl}/functions/v1/send-horse-summary-sms`, {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                          apikey: anonKey,
                          Authorization: `Bearer ${accessToken}`,
                        },
                        body: JSON.stringify({ horse: summarySmsPrompt.horse }),
                      });

                      if (!resp.ok) {
                        const rawBody = await resp.text().catch(() => '');
                        console.warn('Summary SMS failed (modal):', { status: resp.status, statusText: resp.statusText, body: rawBody });
                        let errData = {};
                        try { errData = JSON.parse(rawBody) || {}; } catch (_) { }
                        const errMsg = (errData.error || rawBody || '').toLowerCase();
                        let userMsg = 'Failed to send text. Please check your SMS settings in Profile.';
                        if (resp.status === 409) {
                          if (errMsg.includes('opted out') || errMsg.includes('stop') || errMsg.includes('reply start')) {
                            userMsg = "You previously unsubscribed by texting STOP. To get texts again, text START to the Pre Go Prep number.";
                          } else if (errMsg.includes('not opted in')) {
                            userMsg = 'SMS is turned off in your profile. Turn on SMS in Profile → SMS Notifications to receive texts.';
                          }
                        } else if (resp.status === 403 && errMsg.includes('allowlist')) {
                          userMsg = "Texts aren't enabled for this number right now.";
                        } else if (resp.status === 400 && errMsg.includes('phone')) {
                          userMsg = 'Add a phone number in Profile Settings to receive text summaries.';
                        } else if (resp.status === 400 && errMsg.includes('past race')) {
                          userMsg = 'This race date is in the past. Race-day summary texts can only be sent for upcoming or same-day races.';
                        } else if (resp.status === 401) {
                          userMsg = 'Please sign in again and try again.';
                        }
                        setSummarySmsPrompt({ open: false, sending: false, horse: null });
                        setSnackbar({ open: true, message: userMsg, severity: 'error' });
                        setError(userMsg);
                        setTimeout(() => setError(''), 5000);
                        return;
                      }

                      setSummarySmsPrompt({ open: false, sending: false, horse: null });
                      setSnackbar({ open: true, message: 'Text sent successfully.', severity: 'success' });
                      setError('Text sent successfully!');
                      setTimeout(() => setError(''), 3000);
                    } catch (e) {
                      console.warn('Summary SMS error (modal):', e);
                      setSummarySmsPrompt({ open: false, sending: false, horse: null });
                      const userMsg = 'Failed to send text. Please check your SMS settings in Profile.';
                      setSnackbar({ open: true, message: userMsg, severity: 'error' });
                      setError(userMsg);
                      setTimeout(() => setError(''), 5000);
                    }
                  }}
                  style={{
                    backgroundColor: colors.primary,
                    color: 'white',
                    border: 'none',
                    borderRadius: '10px',
                    padding: '10px 12px',
                    fontWeight: 700,
                    cursor: summarySmsPrompt.sending ? 'not-allowed' : 'pointer',
                    minWidth: '140px',
                  }}
                >
                  {summarySmsPrompt.sending ? 'Sending…' : 'Yes'}
                </button>

                <button
                  type="button"
                  disabled={summarySmsPrompt.sending}
                  onClick={() => setSummarySmsPrompt({ open: false, sending: false, horse: null })}
                  style={{
                    backgroundColor: 'transparent',
                    color: colors.text.primary,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '10px',
                    padding: '10px 12px',
                    fontWeight: 700,
                    cursor: summarySmsPrompt.sending ? 'not-allowed' : 'pointer',
                    minWidth: '140px',
                  }}
                >
                  No
                </button>
              </div>
            </div>
          )}

          {/* Flow 2: After Save Horse, ask if user wants text reminders for this horse */}
          {horseReminderPrompt.open && (
            <div
              style={{
                position: 'absolute',
                left: '50%',
                transform: 'translateX(-50%)',
                bottom: 'calc(100% + 10px)',
                zIndex: 50,
                width: 'min(520px, calc(100vw - 32px))',
                backgroundColor: colors.surface,
                border: `1px solid ${colors.border}`,
                borderRadius: '12px',
                boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
                padding: '16px 18px',
              }}
              role="dialog"
              aria-label={
                horseReminderPrompt.raceInPast
                  ? "Race date in past"
                  : horseReminderPrompt.needsPhoneNumber
                    ? "Add phone number first"
                    : "Enable text reminders for this horse"
              }
            >
              {horseReminderPrompt.raceInPast ? (
                <>
                  <div style={{ fontWeight: 700, color: colors.text.primary, marginBottom: '8px' }}>
                    ⚠️ Race date was in past
                  </div>
                  <div style={{ color: colors.text.secondary, fontSize: '0.9rem', marginBottom: '12px' }}>
                    We can't enable reminders for past races. Please save a horse with a future race date to set up text reminders.
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      gap: '10px',
                      marginTop: '12px',
                      justifyContent: 'flex-end',
                      flexWrap: 'wrap',
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => {
                        setHorseReminderPrompt({ open: false, horseId: null, needsPhoneNumber: false, raceInPast: false });
                      }}
                      style={{
                        backgroundColor: colors.primary,
                        color: 'white',
                        border: 'none',
                        borderRadius: '10px',
                        padding: '10px 12px',
                        fontWeight: 700,
                        cursor: 'pointer',
                        minWidth: '140px',
                      }}
                    >
                      OK
                    </button>
                  </div>
                </>
              ) : horseReminderPrompt.needsPhoneNumber ? (
                <>
                  <div style={{ fontWeight: 700, color: colors.text.primary, marginBottom: '8px' }}>
                    ⚠️ Add phone number first
                  </div>
                  <div style={{ color: colors.text.secondary, fontSize: '0.9rem', marginBottom: '12px' }}>
                    To receive text reminders, please add your phone number and enable SMS notifications in your Profile settings.
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      gap: '10px',
                      marginTop: '12px',
                      justifyContent: 'flex-end',
                      flexWrap: 'wrap',
                    }}
                  >
                    <Link
                      to="/profile"
                      style={{
                        backgroundColor: colors.primary,
                        color: 'white',
                        border: 'none',
                        borderRadius: '10px',
                        padding: '10px 12px',
                        fontWeight: 700,
                        cursor: 'pointer',
                        minWidth: '140px',
                        textDecoration: 'none',
                        textAlign: 'center',
                        display: 'inline-block',
                      }}
                    >
                      Go to Profile
                    </Link>
                    <button
                      type="button"
                      onClick={() => {
                        setHorseReminderPrompt({ open: false, horseId: null, needsPhoneNumber: false, raceInPast: false });
                      }}
                      style={{
                        backgroundColor: 'transparent',
                        color: colors.text.primary,
                        border: `1px solid ${colors.border}`,
                        borderRadius: '10px',
                        padding: '10px 12px',
                        fontWeight: 700,
                        cursor: 'pointer',
                        minWidth: '140px',
                      }}
                    >
                      Maybe Later
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div style={{ fontWeight: 700, color: colors.text.primary, marginBottom: '6px' }}>
                    Would you like text reminders for your therapeutic applications?
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      gap: '10px',
                      marginTop: '12px',
                      justifyContent: 'flex-end',
                      flexWrap: 'wrap',
                    }}
                  >
                    <button
                      type="button"
                      onClick={async () => {
                        // Double-check phone number before enabling (safety check)
                        try {
                          const { data: profile, error: profileError } = await supabase
                            .from('profiles')
                            .select('phone_number, sms_opt_in')
                            .eq('user_id', user.id)
                            .single();

                          if (profileError || !profile || !profile.phone_number || !profile.sms_opt_in) {
                            setError('Please add a phone number and enable SMS in your Profile settings first.');
                            setHorseReminderPrompt({ open: false, horseId: null, needsPhoneNumber: false, raceInPast: false });
                            return;
                          }

                          if (horseReminderPrompt.horseId) {
                            // Persist toggle state to database
                            try {
                              const { error: updateError } = await supabase
                                .from('consolidated_horses')
                                .update({
                                  sms_enabled: true,
                                  sms_disabled_at: null
                                })
                                .eq('id', horseReminderPrompt.horseId)
                                .eq('user_id', user.id);

                              if (updateError) {
                                console.error('Failed to update sms_enabled:', updateError);
                                setError('Failed to save toggle state. Please try again.');
                                return;
                              }
                            } catch (err) {
                              console.error('Error updating sms_enabled:', err);
                              setError('Failed to save toggle state. Please try again.');
                              return;
                            }

                            // Update state after database update succeeds
                            setHorseReminderEnabledMap((prev) => ({
                              ...prev,
                              [horseReminderPrompt.horseId]: true,
                            }));
                          }
                          setHorseReminderPrompt({ open: false, horseId: null, needsPhoneNumber: false, raceInPast: false });
                        } catch (err) {
                          console.error('Error verifying phone number:', err);
                          setError('Failed to enable reminders. Please try again.');
                          setHorseReminderPrompt({ open: false, horseId: null, needsPhoneNumber: false, raceInPast: false });
                        }
                      }}
                      style={{
                        backgroundColor: colors.primary,
                        color: 'white',
                        border: 'none',
                        borderRadius: '10px',
                        padding: '10px 12px',
                        fontWeight: 700,
                        cursor: 'pointer',
                        minWidth: '140px',
                      }}
                    >
                      Yes
                    </button>

                    <button
                      type="button"
                      onClick={async () => {
                        if (horseReminderPrompt.horseId) {
                          // Persist toggle state to database
                          try {
                            const { error: updateError } = await supabase
                              .from('consolidated_horses')
                              .update({
                                sms_enabled: false,
                                sms_disabled_at: new Date().toISOString()
                              })
                              .eq('id', horseReminderPrompt.horseId)
                              .eq('user_id', user.id);

                            if (updateError) {
                              console.error('Failed to update sms_enabled:', updateError);
                              // Continue anyway - don't block user from closing popup
                            }
                          } catch (err) {
                            console.error('Error updating sms_enabled:', err);
                            // Continue anyway - don't block user from closing popup
                          }

                          // Update state
                          setHorseReminderEnabledMap((prev) => ({
                            ...prev,
                            [horseReminderPrompt.horseId]: false,
                          }));
                        }
                        setHorseReminderPrompt({ open: false, horseId: null, needsPhoneNumber: false, raceInPast: false });
                      }}
                      style={{
                        backgroundColor: 'transparent',
                        color: colors.text.primary,
                        border: `1px solid ${colors.border}`,
                        borderRadius: '10px',
                        padding: '10px 12px',
                        fontWeight: 700,
                        cursor: 'pointer',
                        minWidth: '140px',
                      }}
                    >
                      No
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {/*
          <button
            type="button"
            onClick={saveHorse}
            onTouchEnd={(e) => {
              e.preventDefault();
              saveHorse();
            }}
            style={{
              backgroundColor: colors.secondary,
              color: colors.text.primary,
              padding: '12px 24px',
              borderRadius: '8px',
              border: 'none',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              width: '100%',
              WebkitAppearance: 'none',
              WebkitTapHighlightColor: 'transparent',
              touchAction: 'manipulation',
            }}
          >
            {editingHorseId ? 'Update Horse' : 'Save Horse'}
          </button>
          */}

          {hasCalculated && !summarySmsPrompt.open && (
            <div style={{
              color: colors.primary,
              fontSize: '0.9rem',
              textAlign: 'center',
              marginBottom: '12px',
              fontWeight: '600',
              padding: '0 10px'
            }}>
              Note: Click "{editingHorseId ? 'Update Horse' : 'Save Horse'}" to set up your therapeutic text reminders!
            </div>
          )}
          <button
            type="button"
            onClick={hasCalculated ? saveHorse : handleSubmit}
            onTouchEnd={(e) => {
              e.preventDefault();
              if (hasCalculated) {
                saveHorse();
              } else {
                handleSubmit(e);
              }
            }}
            style={{
              backgroundColor: colors.secondary,
              color: colors.text.primary,
              padding: '12px 24px',
              borderRadius: '8px',
              border: 'none',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              width: '100%',
              WebkitAppearance: 'none',
              WebkitTapHighlightColor: 'transparent',
              touchAction: 'manipulation',
            }}
          >
            {hasCalculated
              ? (editingHorseId ? 'Update Horse' : 'Save Horse')
              : 'Calculate Times'}
          </button>
        </div>

        {editingHorseId && (
          <button
            type="button"
            onClick={() => {
              setEditingHorseId(null);
              setClasses([]);
              setFormData({
                raceName: '',
                raceDate: '',
                raceStartTime: '',
                estRunsPerHour: '',
                raceTimezone: '',
                horseName: '',
                riderName: '',
                className: '',
                draw: '',
                breakDrawNumber: '',
                breakDuration: '',
                hoursBeforeRun1: '',
                hoursBeforeRun2: '',
                hoursBeforeRun3: '',
                totalRuns: '',
                averageRunTime: '',
                isReverseOrder: false
              });
              setResults(null);
              setError('');
            }}
            style={{
              backgroundColor: colors.warning,
              color: colors.surface,
              padding: '12px 24px',
              borderRadius: '8px',
              border: 'none',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              width: '100%',
              maxWidth: '300px'
            }}
          >
            Cancel Edit
          </button>
        )}
      </div>

      {/* Saved Horses Section */}
      <div style={commonStyles.section}>
        <SavedHorses
          savedHorses={savedHorses}
          onEditHorse={onEditHorse}
          onDeleteHorse={(id) => {
            setDeleteHorseConfirm({ open: true, horseId: id });
          }}
          onCloneHorse={cloneHorse}
          onSendSummarySms={sendHorseSummarySms}
          colors={colors}
          commonStyles={commonStyles}
          setError={setError}
          horseReminderEnabledMap={horseReminderEnabledMap}
          onToggleHorseReminder={async (horseId) => {
            if (!horseId) return;

            const horse = savedHorses.find(h => h.id === horseId);
            if (!horse) return;

            const currentlyEnabled = horseReminderEnabledMap?.[horseId];
            if (!currentlyEnabled && !isSubActive) {
              // Check if user has a phone number before showing upgrade modal
              const { data: prof } = await supabase.from('profiles').select('phone_number').eq('user_id', user.id).single();
              if (!prof?.phone_number) {
                setPhoneNeeded(true);
                return;
              }
              setUpgradeModal({ open: true });
              return;
            }

            // Check if horse has any therapeutic times
            const hasTherapeuticTimes = [1, 2, 3].some(num => {
              const time = horse[`time_to_give_tool${num}`];
              return time && time !== 'N/A';
            });

            // If no therapeutic times, force master toggle OFF
            if (!hasTherapeuticTimes) {
              setHorseReminderEnabledMap((prev) => ({
                ...prev,
                [horseId]: false,
              }));

              // Also update DB
              try {
                await supabase
                  .from('consolidated_horses')
                  .update({
                    sms_enabled: false,
                    sms_disabled_at: new Date().toISOString()
                  })
                  .eq('id', horseId)
                  .eq('user_id', user.id);
              } catch (err) {
                console.error('Error updating sms_enabled:', err);
              }
              return;
            }

            const currentState = horseReminderEnabledMap?.[horseId];
            const willBeEnabled = !currentState;

            // If turning ON, check phone number and SMS opt-in first
            if (willBeEnabled) {
              try {
                const { data: profile, error: profileError } = await supabase
                  .from('profiles')
                  .select('phone_number, sms_opt_in, sms_opt_out_source')
                  .eq('user_id', user.id)
                  .single();

                if (profileError || !profile) {
                  setError('Failed to load profile. Please try again.');
                  return;
                }

                // Check for STOP opt-out
                const isStopOptedOut = profile.sms_opt_out_source === 'stop' && !profile.sms_opt_in;
                if (isStopOptedOut) {
                  setError('You unsubscribed by texting STOP. To get texts again, text START to the Pre Go Prep number.');
                  return;
                }

                if (!profile.sms_opt_in) {
                  setError('Please enable SMS notifications in your Profile settings first.');
                  return;
                }

                if (!profile.phone_number) {
                  setError('Please add a phone number in your Profile settings first.');
                  return;
                }
              } catch (err) {
                console.error('Error checking SMS eligibility:', err);
                setError('Failed to verify SMS settings. Please try again.');
                return;
              }
            }

            // CRITICAL FIX: Persist toggle state to database BEFORE updating state
            try {
              const { error: updateError } = await supabase
                .from('consolidated_horses')
                .update({
                  sms_enabled: willBeEnabled,
                  sms_disabled_at: willBeEnabled ? null : new Date().toISOString()
                })
                .eq('id', horseId)
                .eq('user_id', user.id);

              if (updateError) {
                console.error('Failed to update sms_enabled:', updateError);
                setError('Failed to save toggle state. Please try again.');
                return; // Don't update state if DB update fails
              }
            } catch (err) {
              console.error('Error updating sms_enabled:', err);
              setError('Failed to save toggle state. Please try again.');
              return; // Don't update state if DB update fails
            }

            // Only update state after database update succeeds
            setHorseReminderEnabledMap((prev) => ({
              ...prev,
              [horseId]: willBeEnabled,
            }));
          }}
        />
      </div>

      {/* Delete Horse Confirmation Dialog */}
      <Dialog
        open={deleteHorseConfirm.open}
        onClose={() => setDeleteHorseConfirm({ open: false, horseId: null })}
        aria-labelledby="delete-horse-dialog-title"
        aria-describedby="delete-horse-dialog-description"
        PaperProps={{
          style: {
            borderRadius: '8px',
            padding: '8px',
            minWidth: '400px',
            maxWidth: '90vw',
          },
        }}
      >
        <DialogTitle
          id="delete-horse-dialog-title"
          style={{
            color: colors.primary,
            fontWeight: '600',
            fontSize: '1.25rem',
            paddingBottom: '8px',
          }}
        >
          Delete Saved Horse
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            id="delete-horse-dialog-description"
            style={{
              color: '#495057',
              fontSize: '0.95rem',
              lineHeight: 1.5,
            }}
          >
            Are you sure you want to delete this saved horse? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions style={{ padding: '16px 24px', gap: '12px' }}>
          <Button
            onClick={() => setDeleteHorseConfirm({ open: false, horseId: null })}
            style={{
              color: colors.primary,
              textTransform: 'none',
              fontWeight: '500',
              padding: '8px 16px',
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDeleteHorse}
            variant="contained"
            style={{
              backgroundColor: '#dc3545',
              color: '#ffffff',
              textTransform: 'none',
              fontWeight: '500',
              padding: '8px 16px',
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Disclaimer */}
      <div style={{
        textAlign: 'center',
        padding: '20px',
        fontSize: '14px',
        color: '#b91c1c',
        marginTop: '20px',
        fontWeight: '500'
      }}>
        <em>Pre Go Prep provides estimated run times and therapeutic application timing as a guideline only and does not guarantee accuracy. These estimates do not replace official performance schedules and should not be solely relied upon for competition planning or therapeutic application. Pre Go Prep is not responsible for missed Performance Go times, miscalculations, or any consequences resulting from the use of these estimates. It is the user's sole responsibility to track, monitor, and confirm their official Performance Go time with the event producer.</em>
      </div>

      {/* Global Snackbar for success/error messages */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{
            width: '100%',
            alignItems: 'center',
            '& .MuiAlert-message': {
              display: 'flex',
              alignItems: 'center',
              padding: 0,
            },
            '& .MuiAlert-action': {
              alignItems: 'center',
              paddingTop: 0,
            },
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Dialog
        open={phoneNeeded}
        onClose={() => setPhoneNeeded(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: '12px',
            padding: '8px',
          },
        }}
      >
        <DialogContent style={{ textAlign: 'center', paddingTop: '32px', paddingBottom: '8px' }}>
          <div style={{
            width: '56px',
            height: '56px',
            borderRadius: '50%',
            backgroundColor: '#eef2ff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
            fontSize: '24px',
          }}>
            📱
          </div>
          <DialogTitle style={{
            color: '#29458d',
            fontWeight: 600,
            fontSize: '20px',
            padding: '0 0 8px 0',
          }}>
            Phone number required
          </DialogTitle>
          <DialogContentText style={{
            color: '#6c757d',
            fontSize: '14px',
            lineHeight: 1.6,
          }}>
            To subscribe to SMS reminders, please add your phone number on your Profile page first.
          </DialogContentText>
        </DialogContent>
        <DialogActions style={{
          padding: '16px 24px 24px',
          justifyContent: 'center',
          gap: '12px',
        }}>
          <button
            type="button"
            onClick={() => setPhoneNeeded(false)}
            style={{
              color: '#29458d',
              backgroundColor: 'transparent',
              border: '1px solid #29458d',
              fontSize: '16px',
              fontWeight: 400,
              padding: '10px 24px',
              borderRadius: '6px',
              cursor: 'pointer',
            }}
          >
            Not now
          </button>
          <Link
            to="/profile"
            onClick={() => setPhoneNeeded(false)}
            style={{
              backgroundColor: '#29458d',
              color: '#fff',
              fontSize: '16px',
              fontWeight: 400,
              padding: '10px 24px',
              borderRadius: '6px',
              textDecoration: 'none',
              display: 'inline-block',
            }}
          >
            Go to Profile
          </Link>
        </DialogActions>
      </Dialog>

      <UpgradeModal
        open={upgradeModal.open}
        reason={upgradeModal.reason}
        source={upgradeModal.source}
        onPhoneNeeded={() => {
          setUpgradeModal({ open: false });
          setPhoneNeeded(true);
        }}
        onClose={() => {
          if (upgradeModal.source === 'save_horse') {
            localStorage.setItem('pgp_save_paywall_dismissed_at', String(Date.now()));
          }
          setUpgradeModal({ open: false });
        }}
      />
    </div>
  );
};

export default Calculations; 